import { state } from '../shared/state';
import { $, t } from '../shared/index';
import { registerConversationSessionOpener } from '../shared/app-hooks';
import { switchView } from '../shared/navigation';
import { sessionStore, hydrateSessionFromServer, type SessionRuntime } from './session-store';
import { applyCategorySystemPrompt } from '../shared/system-prompt';
import { restoreSessionAttachments } from './session-attachments';
import { refreshSessionIndicators } from './session-indicators';
import { hasUnreadDeferred, markDeferredSeen } from './deferred-unread';
import { updateContextBadge, showToast, setWelcomeVisible, resizeCategorySelect } from '../shared/ui';
import type { SessionData, SessionSummary } from '../shared/types';

// --- Inversion points (ADR-002 phase C): session is the frontend L1
// capability and must not import the conversation engine. Conversation registers its UI here
// on module evaluation; generation-manager registers the switch handler.
export interface SessionConversationUi {
  renderActiveConversationView(): void;
  startNewConversation(reloadList?: boolean): void | Promise<void>;
}

export interface SessionSearchUi {
  clear(): void;
  getHighlightedSessionId(): string | null;
  setWrapVisible(visible: boolean): void;
}

let conversationUi: SessionConversationUi | null = null;
let searchUi: SessionSearchUi | null = null;
let activeSessionSwitchedHandler: () => void = () => {};

export function registerSessionConversationUi(ui: SessionConversationUi): void {
  conversationUi = ui;
}

export function registerSessionSearchUi(ui: SessionSearchUi): void {
  searchUi = ui;
}

export function registerActiveSessionSwitchedHandler(fn: () => void): void {
  activeSessionSwitchedHandler = fn;
}

const isGuestMode = () => localStorage.getItem('avgexpert_guest_mode') === '1';


function deriveSessionTitle(session: Pick<SessionRuntime, 'history' | 'title'>) {
  const firstMsg = session.history.find((m) => m.role === 'user')?.content || '';
  const display = firstMsg.includes('\n\n---\n\n')
    ? firstMsg.split('\n\n---\n\n').pop() || firstMsg
    : firstMsg;
  return display.slice(0, 40).trim() + (display.length > 40 ? '...' : '') || t('new_conversation');
}

export const SessionManager = {

  applyDerivedTitle(id: string) {
    const session = sessionStore.getSession(id);
    if (!session || session.history.length === 0) return;
    const currentTitle = session.title;
    if (currentTitle && currentTitle !== t('new_conversation') && currentTitle !== 'Conversation') return;
    const title = deriveSessionTitle(session);
    if (title !== currentTitle) {
      session.title = title;
      session.dirty = true;
    }
  },

  async loadList() {
    if (isGuestMode()) {
      try {
        const cached = sessionStorage.getItem('avgexpert_guest_sessions');
        const sessions = cached ? (JSON.parse(cached) as SessionSummary[]) : [];
        this.renderList(sessions);
      } catch (err) {
        console.error('Failed to load guest sessions', err);
        this.renderList([]);
      }
      return;
    }

    try {
      const r = await fetch('/api/sessions', {
        headers: { Authorization: 'Bearer ' + state.authToken },
      });
      if (r.ok) {
        const sessions = await r.json() as SessionSummary[];
        const serverGenerating = sessions
          .filter((s) => s.generating && sessionStore.shouldTrustServerGenerating(s.id))
          .map((s) => s.id);
        sessionStore.setServerGeneratingSessions(serverGenerating);
        this.renderList(sessions);
        localStorage.setItem(
          `avgexpert_${state.currentUser?.username}_sessions_cache`,
          JSON.stringify(sessions),
        );
      } else if (r.status === 401) {
        return;
      }
    } catch (err) {
      console.error('Failed to load sessions', err);
      const cached = localStorage.getItem(`avgexpert_${state.currentUser?.username}_sessions_cache`);
      if (cached) this.renderList(JSON.parse(cached) as SessionSummary[]);
    }
  },

  async loadSession(id: string) {
    // Список чатов виден из любого раздела, поэтому открытие чата само ведёт в
    // «Разговор» — иначе клик по чату из Диалога или Видео молча грузил его
    // в невидимую вкладку. Вызов идемпотентен: из «Разговора» ничего не меняет.
    await switchView('conversation');
    sessionStore.captureDraftFromUI();
    const prev = sessionStore.getActiveSession();
    if (prev) {
      await this.saveSession(prev.id);
    }

    if (isGuestMode()) {
      try {
        const cached = sessionStorage.getItem('avgexpert_guest_sessions_data_' + id);
        if (cached) {
          const data = JSON.parse(cached) as SessionData;
          const runtime = sessionStore.getSession(id);
          const isStreaming = runtime?.generation.status === 'streaming'
            || sessionStore.isServerGenerating(id);
          const keepLocal = runtime && (
            isStreaming
            || runtime.dirty
          );

          if (!keepLocal) {
            hydrateSessionFromServer(id, data);
          }

          sessionStore.setActiveSession(id);
          const messagesEl = $('messages');
          const attachedDocsEl = $('attached-docs');
          if (messagesEl) messagesEl.textContent = '';
          if (attachedDocsEl) attachedDocsEl.textContent = '';

          const session = sessionStore.getActiveSession()!;
          const conversationSessionCat = $<HTMLSelectElement>('conversation-session-category');
          const sessionCategory = data.category || '';
          if (conversationSessionCat) {
            conversationSessionCat.value = sessionCategory;
          }
          session.category = sessionCategory;
          resizeCategorySelect();
          applyCategorySystemPrompt(sessionCategory);

          await restoreSessionAttachments(id);
          conversationUi?.renderActiveConversationView();
          updateContextBadge();
          activeSessionSwitchedHandler();
          this.loadList();
        }
      } catch (err) {
        console.error('Failed to load guest session', err);
        showToast(t('load_failed'));
      }
      return;
    }

    try {
      const r = await fetch('/api/sessions/' + id, {
        headers: { Authorization: 'Bearer ' + state.authToken },
      });
      if (r.ok) {
        const data = await r.json() as SessionData;
        const runtime = sessionStore.getSession(id);
        const serverMessages = (data.messages || []).filter((m) => m.role !== 'system');
        const isStreaming = runtime?.generation.status === 'streaming'
          || sessionStore.isServerGenerating(id);
        const keepLocal = runtime && (
          isStreaming
          || runtime.dirty
          || runtime.history.length > serverMessages.length
        );

        if (!keepLocal) {
          hydrateSessionFromServer(id, data);
        } else if (runtime) {
          runtime.title = data.title || runtime.title;
          runtime.category = data.category || runtime.category;
        }

        sessionStore.setActiveSession(id);
        const messagesEl = $('messages');
        const attachedDocsEl = $('attached-docs');
        if (messagesEl) messagesEl.textContent = '';
        if (attachedDocsEl) attachedDocsEl.textContent = '';

        const session = sessionStore.getActiveSession()!;

        const conversationSessionCat = $<HTMLSelectElement>('conversation-session-category');
        const sessionCategory = data.category || state.currentUser?.category || '';
        if (conversationSessionCat) {
          conversationSessionCat.value = sessionCategory;
        }
        session.category = sessionCategory;
        resizeCategorySelect();
        applyCategorySystemPrompt(sessionCategory);

        await restoreSessionAttachments(id);
        conversationUi?.renderActiveConversationView();
        updateContextBadge();
        activeSessionSwitchedHandler();
        // Сессию открыли — доставленные отложенные ответы человек увидел.
        markDeferredSeen(id, session.history.filter((m) => m.deferred_run_id).map((m) => m.id || ''));
        this.loadList();
      }
    } catch {
      showToast(t('load_failed'));
    }
  },

  async saveCurrent() {
    const active = sessionStore.getActiveSession();
    if (!active) return;
    await this.saveSession(active.id);
  },

  async saveSession(id: string) {
    const session = sessionStore.getSession(id);
    if (!session || session.history.length === 0) return;
    if (!session.dirty) return;


    let title = session.title;
    if (!title || title === t('new_conversation') || title === 'Conversation' || session.history.length <= 2) {
      title = deriveSessionTitle(session);
      session.title = title;
    }

    if (isGuestMode()) {
      try {
        const syncEl = $('sync-indicator');
        if (syncEl) syncEl.classList.remove('hidden');

        const activeCategory = session.category
          || $<HTMLSelectElement>('conversation-session-category')?.value;

        const data = {
          id,
          title,
          messages: session.history,
          category: activeCategory || null,
          updatedAt: Date.now(),
        };

        sessionStorage.setItem('avgexpert_guest_sessions_data_' + id, JSON.stringify(data));

        const listCached = sessionStorage.getItem('avgexpert_guest_sessions');
        const summaries: SessionSummary[] = listCached ? JSON.parse(listCached) : [];
        const idx = summaries.findIndex((s) => s.id === id);
        const summary: SessionSummary = {
          id,
          title,
          updatedAt: data.updatedAt,
          category: activeCategory || undefined,
          generating: false,
        };
        if (idx >= 0) {
          summaries[idx] = summary;
        } else {
          summaries.unshift(summary);
        }
        sessionStorage.setItem('avgexpert_guest_sessions', JSON.stringify(summaries));
        session.dirty = false;
        this.loadList();
        if (syncEl) setTimeout(() => syncEl.classList.add('hidden'), 500);
      } catch (err) {
        console.error('Failed to save guest session', err);
        $('sync-indicator')?.classList.add('hidden');
      }
      return;
    }

    try {
      const syncEl = $('sync-indicator');
      if (syncEl) syncEl.classList.remove('hidden');

      const activeCategory = session.category
        || $<HTMLSelectElement>('conversation-session-category')?.value
        || state.currentUser?.category;

      await fetch('/api/sessions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + state.authToken || '',
        },
        body: JSON.stringify({
          id,
          title,
          messages: session.history,
          category: activeCategory,
          updatedAt: Date.now(),
        }),
      });
      session.dirty = false;
      this.loadList();
      if (syncEl) setTimeout(() => syncEl.classList.add('hidden'), 500);
    } catch (err) {
      console.error('Failed to save session', err);
      $('sync-indicator')?.classList.add('hidden');
    }
  },

  refreshSessionIndicators() {
    refreshSessionIndicators();
  },

  async renameSession(id: string, newTitle: string) {
    if (!newTitle) return;

    if (isGuestMode()) {
      try {
        const syncEl = $('sync-indicator');
        if (syncEl) syncEl.classList.remove('hidden');

        const session = sessionStore.getSession(id);
        if (session) session.title = newTitle;

        const cached = sessionStorage.getItem('avgexpert_guest_sessions_data_' + id);
        if (cached) {
          const data = JSON.parse(cached);
          data.title = newTitle;
          sessionStorage.setItem('avgexpert_guest_sessions_data_' + id, JSON.stringify(data));
        }

        const listCached = sessionStorage.getItem('avgexpert_guest_sessions');
        if (listCached) {
          const summaries: SessionSummary[] = JSON.parse(listCached);
          const idx = summaries.findIndex((s) => s.id === id);
          if (idx >= 0) {
            summaries[idx].title = newTitle;
            sessionStorage.setItem('avgexpert_guest_sessions', JSON.stringify(summaries));
          }
        }
        await this.loadList();
        if (syncEl) setTimeout(() => syncEl.classList.add('hidden'), 500);
      } catch (err) {
        console.error('Failed to rename guest session', err);
        $('sync-indicator')?.classList.add('hidden');
      }
      return;
    }

    try {
      const syncEl = $('sync-indicator');
      if (syncEl) syncEl.classList.remove('hidden');

      const r = await fetch('/api/sessions/' + id, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + state.authToken || '',
        },
        body: JSON.stringify({ title: newTitle }),
      });

      if (r.ok) {
        const session = sessionStore.getSession(id);
        if (session) session.title = newTitle;
        await this.loadList();
      } else {
        showToast(t('rename_failed'));
      }
      if (syncEl) setTimeout(() => syncEl.classList.add('hidden'), 500);
    } catch (err) {
      console.error('Rename error:', err);
      showToast(t('rename_failed'));
      $('sync-indicator')?.classList.add('hidden');
    }
  },

  async deleteSession(id: string, ev?: Event) {
    if (ev) ev.stopPropagation();
    if (!confirm(t('confirm_delete'))) return;

    if (isGuestMode()) {
      try {
        sessionStore.deleteSession(id);
        sessionStorage.removeItem('avgexpert_guest_sessions_data_' + id);

        const listCached = sessionStorage.getItem('avgexpert_guest_sessions');
        if (listCached) {
          const summaries: SessionSummary[] = JSON.parse(listCached);
          const filtered = summaries.filter((s) => s.id !== id);
          sessionStorage.setItem('avgexpert_guest_sessions', JSON.stringify(filtered));
        }

        if (sessionStore.activeSessionId === id) {
          void conversationUi?.startNewConversation(false);
        } else {
          await this.loadList();
        }
      } catch (err) {
        console.error('Failed to delete guest session', err);
        showToast(t('delete_failed'));
      }
      return;
    }

    try {
      const r = await fetch('/api/sessions/' + id, {
        method: 'DELETE',
        headers: { Authorization: 'Bearer ' + state.authToken || '' },
      });
      if (r.ok) {
        if (sessionStore.activeSessionId === id) {
          sessionStore.deleteSession(id);
          void conversationUi?.startNewConversation(false);
        } else {
          sessionStore.deleteSession(id);
        }
        await this.loadList();
      }
    } catch (err) {
      console.error('Delete error:', err);
      showToast(t('delete_failed'));
    }
  },

  async _fetchMessages(id: string): Promise<SessionData | null> {
    if (isGuestMode()) {
      try {
        const cached = sessionStorage.getItem('avgexpert_guest_sessions_data_' + id);
        return cached ? (JSON.parse(cached) as SessionData) : null;
      } catch {
        return null;
      }
    }

    const r = await fetch('/api/sessions/' + id, {
      headers: { Authorization: 'Bearer ' + state.authToken || '' },
    });
    if (!r.ok) return null;
    return await r.json() as SessionData;
  },

  _buildCsv(sessionData: SessionData) {
    const rows: string[][] = [['Дата-Время', 'Вопрос', 'Ответ']];
    const msgs = sessionData.messages || [];

    for (let i = 0; i < msgs.length; i++) {
      if (msgs[i].role === 'user') {
        const question = msgs[i].content || '';
        const answer = (msgs[i + 1] && msgs[i + 1].role === 'assistant')
          ? msgs[i + 1].content || ''
          : '';

        const msgTs = msgs[i].timestamp;
        const ts = msgTs != null
          ? new Date(msgTs)
          : new Date(Number(sessionData.updatedAt) || Date.now());

        const dateStr = ts.toLocaleDateString('ru-RU');
        const timeStr = ts.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
        const dtStr = `${dateStr} ${timeStr}`;

        rows.push([dtStr, question, answer]);
        if (answer) i++;
      }
    }

    return rows.map((r) =>
      r.map((cell) => '"' + String(cell).replace(/"/g, '""').replace(/\n/g, ' ') + '"')
        .join(';'),
    ).join('\r\n');
  },

  _downloadCsv(filename: string, csvContent: string) {
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  async exportSession(id: string, ev?: Event) {
    if (ev) ev.stopPropagation();
    try {
      const data = await this._fetchMessages(id);
      if (!data) { showToast(t('export_failed')); return; }
      const safeName = (data.title || 'conversation').replace(/[<>:"/\\|?*]/g, '_');
      const csv = this._buildCsv(data);
      this._downloadCsv(`${safeName}.csv`, csv);
    } catch (err) {
      console.error('Export error:', err);
      showToast(t('export_failed'));
    }
  },

  async deleteGroup(groupName: string, items: SessionSummary[]) {
    if (!confirm(t('confirm_delete_group'))) return;
    let currentDeleted = false;

    if (isGuestMode()) {
      try {
        for (const s of items) {
          if (sessionStore.activeSessionId === s.id) currentDeleted = true;
          sessionStore.deleteSession(s.id);
          sessionStorage.removeItem('avgexpert_guest_sessions_data_' + s.id);
        }
        const listCached = sessionStorage.getItem('avgexpert_guest_sessions');
        if (listCached) {
          const summaries: SessionSummary[] = JSON.parse(listCached);
          const idsToDelete = new Set(items.map((s) => s.id));
          const filtered = summaries.filter((s) => !idsToDelete.has(s.id));
          sessionStorage.setItem('avgexpert_guest_sessions', JSON.stringify(filtered));
        }
        if (currentDeleted) {
          void conversationUi?.startNewConversation(false);
        }
        await this.loadList();
      } catch (err) {
        console.error('Failed to delete guest group', err);
        showToast(t('delete_failed'));
      }
      return;
    }

    try {
      for (const s of items) {
        const r = await fetch('/api/sessions/' + s.id, {
          method: 'DELETE',
          headers: { Authorization: 'Bearer ' + state.authToken || '' },
        });
        if (r.ok && sessionStore.activeSessionId === s.id) currentDeleted = true;
        sessionStore.deleteSession(s.id);
      }
      if (currentDeleted) {
        void conversationUi?.startNewConversation(false);
      }
      await this.loadList();
    } catch (err) {
      console.error('Group delete error:', err);
      showToast(t('delete_failed'));
    }
  },

  async exportGroup(groupName: string, items: SessionSummary[]) {
    try {
      const allRows: string[][] = [['Дата-Время', 'Разговор', 'Вопрос', 'Ответ']];

      for (const s of items) {
        const data = await this._fetchMessages(s.id);
        if (!data) continue;
        const msgs = data.messages || [];

        for (let i = 0; i < msgs.length; i++) {
          if (msgs[i].role === 'user') {
            const question = msgs[i].content || '';
            const answer = (msgs[i + 1] && msgs[i + 1].role === 'assistant')
              ? msgs[i + 1].content || ''
              : '';

            const msgTs = msgs[i].timestamp;
            const ts = msgTs != null
              ? new Date(msgTs)
              : new Date(Number(data.updatedAt) || Date.now());
            const dtStr = `${ts.toLocaleDateString('ru-RU')} ${ts.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;

            allRows.push([dtStr, data.title || s.id, question, answer]);
            if (answer) i++;
          }
        }
      }

      const csvContent = allRows.map((r) =>
        r.map((cell) => '"' + String(cell).replace(/"/g, '""').replace(/\n/g, ' ') + '"')
          .join(';'),
      ).join('\r\n');

      const safeName = groupName.replace(/\s+/g, '_');
      this._downloadCsv(`conversations_${safeName}.csv`, csvContent);
    } catch (err) {
      console.error('Group export error:', err);
      showToast(t('export_failed'));
    }
  },

  scrollActiveSessionIntoView() {
    const list = $('sessions-list');
    const activeId = sessionStore.activeSessionId;
    if (!list || !activeId) return;
    const activeItem = list.querySelector(`.session-item[data-id="${activeId}"]`);
    activeItem?.scrollIntoView({ block: 'nearest' });
  },

  renderList(sessions: SessionSummary[]) {
    const list = $('sessions-list');
    if (!list) return;
    list.textContent = '';
    searchUi?.setWrapVisible(sessions.length > 0);

    if (sessions.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'sessions-empty';
      empty.textContent = 'Нет истории разговоров';
      list.appendChild(empty);
      return;
    }

    const highlightedId = searchUi?.getHighlightedSessionId() ?? null;
    const groups: Record<string, SessionSummary[]> = {
      'Сегодня': [],
      'Вчера': [],
      'На этой неделе': [],
      'В этом месяце': [],
      'Ранее': [],
    };

    const now = new Date();
    sessions.forEach((s) => {
      const date = new Date(Number(s.updatedAt) || Date.now());
      const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays === 0) groups['Сегодня'].push(s);
      else if (diffDays === 1) groups['Вчера'].push(s);
      else if (diffDays < 7) groups['На этой неделе'].push(s);
      else if (diffDays < 30) groups['В этом месяце'].push(s);
      else groups['Ранее'].push(s);
    });

    Object.entries(groups).forEach(([groupName, items]) => {
      if (items.length === 0) return;

      const groupHeader = document.createElement('div');
      groupHeader.className = 'session-group-header';

      const label = document.createElement('div');
      label.className = 'session-group-label';
      label.textContent = groupName;

      const groupActions = document.createElement('div');
      groupActions.className = 'session-group-actions';

      const saveAllBtn = document.createElement('button');
      saveAllBtn.className = 'group-action-btn';
      saveAllBtn.title = t('save_all');
      saveAllBtn.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/>
        <line x1="12" y1="15" x2="12" y2="3"/>
      </svg>`;
      saveAllBtn.addEventListener('click', (ev) => { ev.stopPropagation(); this.exportGroup(groupName, items); });

      const delAllBtn = document.createElement('button');
      delAllBtn.className = 'group-action-btn delete';
      delAllBtn.title = t('delete_all');
      delAllBtn.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
      </svg>`;
      delAllBtn.addEventListener('click', (ev) => { ev.stopPropagation(); this.deleteGroup(groupName, items); });

      groupActions.appendChild(saveAllBtn);
      groupActions.appendChild(delAllBtn);
      groupHeader.appendChild(label);
      groupHeader.appendChild(groupActions);
      list.appendChild(groupHeader);

      items.forEach((s) => {
        const runtime = sessionStore.getSession(s.id);
        const isStreaming = runtime?.generation.status === 'streaming'
          || sessionStore.isServerGenerating(s.id);
        const isIndexing = runtime
          ? runtime.attachedDocs.some((d) => d.id && (d.status === 'pending' || d.status === 'processing'))
          : false;

        const item = document.createElement('div');
        item.className = 'session-item'
          + (sessionStore.activeSessionId === s.id ? ' active' : '')
          + (highlightedId === s.id ? ' search-selected' : '');
        item.dataset.id = s.id;

        const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        iconSvg.setAttribute('width', '14');
        iconSvg.setAttribute('height', '14');
        iconSvg.setAttribute('viewBox', '0 0 24 24');
        iconSvg.setAttribute('fill', 'none');
        iconSvg.setAttribute('stroke', 'currentColor');
        iconSvg.setAttribute('stroke-width', '2');
        iconSvg.setAttribute('class', 'session-icon');
        iconSvg.innerHTML = '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>';

        const titleSpan = document.createElement('span');
        titleSpan.className = 'session-title';
        titleSpan.title = s.title;
        titleSpan.textContent = s.title;

        const statusIcons = document.createElement('span');
        statusIcons.className = 'session-status-icons';

        const genIcon = document.createElement('span');
        genIcon.className = 'session-generating-icon' + (isStreaming ? '' : ' hidden');
        genIcon.title = 'Генерация ответа';
        genIcon.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>';

        const idxIcon = document.createElement('span');
        idxIcon.className = 'session-indexing-icon' + (isIndexing && !isStreaming ? '' : ' hidden');
        idxIcon.title = 'Индексация документов';
        idxIcon.textContent = '📎';

        const defIcon = document.createElement('span');
        defIcon.className = 'session-deferred-icon' + (hasUnreadDeferred(s.id) ? '' : ' hidden');
        defIcon.title = 'Пришёл отложенный ответ';
        defIcon.textContent = '✉️';

        statusIcons.appendChild(genIcon);
        statusIcons.appendChild(idxIcon);
        statusIcons.appendChild(defIcon);

        const actions = document.createElement('div');
        actions.className = 'session-actions';

        const saveBtn = document.createElement('button');
        saveBtn.className = 'session-action-btn';
        saveBtn.title = t('save');
        saveBtn.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7 10 12 15 17 10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>`;
        saveBtn.addEventListener('click', (ev) => this.exportSession(s.id, ev));

        const renBtn = document.createElement('button');
        renBtn.className = 'session-action-btn';
        renBtn.title = t('rename');
        renBtn.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>`;
        renBtn.addEventListener('click', (ev) => {
          ev.stopPropagation();
          const newTitle = prompt(t('rename_prompt'), s.title);
          if (newTitle && newTitle.trim() && newTitle.trim() !== s.title) {
            this.renameSession(s.id, newTitle.trim());
          }
        });

        const delBtn = document.createElement('button');
        delBtn.className = 'session-action-btn delete';
        delBtn.title = t('delete');
        delBtn.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
        </svg>`;
        delBtn.addEventListener('click', (ev) => this.deleteSession(s.id, ev));

        actions.appendChild(saveBtn);
        actions.appendChild(renBtn);
        actions.appendChild(delBtn);

        item.appendChild(iconSvg);
        item.appendChild(titleSpan);
        item.appendChild(statusIcons);
        item.appendChild(actions);

        item.addEventListener('click', () => {
          searchUi?.clear();
          this.loadSession(s.id);
        });
        list.appendChild(item);
      });
    });

    this.scrollActiveSessionIntoView();
  },
};

// Точка инверсии (ADR-002 phase C): продукты (Диалог — «Сохранить в Разговор»)
// открывают чат через shared/app-hooks, а не импортом этой возможности вбок.
registerConversationSessionOpener((sessionId) => SessionManager.loadSession(sessionId));

window.addEventListener('storage', (ev: StorageEvent) => {
  if (ev.key === 'avgexpert_token' && !ev.newValue) {
    window.location.reload();
  }
});
